<template>
  <span :class="['chip', `chip--${type}`]" :style="chipStyle">
   <div :class="{'chip-label':wrapText}">{{ label }}</div> 
  </span>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'default',
    validator: (value) =>
      ['primary', 'secondary', 'success', 'danger', 'warning', 'info', 'default'].includes(value),
  },
  customColor: {
    type: String,
    default: null,
  },
  customBackgroundColor: {
    type: String,
    default: null,
  },
  wrapText:{
    type:Boolean,
    default:false
  },
  style: {
    type: Object,
    default: null,
  },
});

const chipStyle = computed(() => {
  const style = {};
  if (props.customBackgroundColor) {
    style.backgroundColor = props.customBackgroundColor;
  }
  if (props.customColor) {
    style.color = props.customColor;
  }

  // If custom colors are provided, they might need to override border color too,
  // or ensure contrast. For simplicity, we'll let CSS classes define initial border
  // and custom background/text colors override appearance.
  // If a custom background is set, we might want to ensure the border matches or is contrasting.
  // For now, customBackgroundColor will override the class background, and customColor the class text.
  return {...style,...props.style};
});
</script>

<style scoped>
.chip {

  display: inline-flex;
  align-items: center;

  padding: 0.35em 0.65em; /* Adjusted padding for a slightly more compact look */
  border-radius: 4px;
  font-size: 0.8rem; /* Slightly smaller font size */
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  cursor: default;
  border: 1px solid transparent;
}

/* Default styles */
.chip--default {
  background-color: #f0f0f0; /* Lighter grey */
  color: #555555; /* Darker grey text for better contrast */
  border-color: #dcdcdc; /* Softer border */
}

/* Predefined color types */
.chip--primary {
  background-color: #007bff;
  color: white;
  border-color: #007bff;
}

.chip--secondary {
  background-color: #6c757d;
  color: white;
  border-color: #6c757d;
}

.chip--success {
  background-color: #d4edda;
  color: #155724;
  border-color: #d4edda;
}

.chip--danger {
  background-color: #f8d7da;
  color: #721c24;
  border-color: #f8d7da;
}

.chip--warning {
  background-color: #fff3cd;
  color: #856404; /* Dark text for yellow for better readability */
  border-color: #fff3cd;
}

.chip--info {
  background-color: #17a2b8;
  color: white;
  border-color: #17a2b8;
}

.chip-label {
  white-space:wrap;
}
</style>
